export interface AdjustmentAmountLike {
    number?: string | null;
    formatted?: string | null;
}

export interface AdjustmentLike {
    type: string;
    label: string;
    amount: AdjustmentAmountLike | string;
    percentage: string | null;
}

const NO_VAT_RELATION_VALUES = ['B', 'N', 'X'];

/**
 * Determines whether VAT should be shown for a relation and address.
 */
export const isVatApplicable = (vat: string|null|undefined, countryCode: string|null|undefined): boolean => {
    const vatCode = vat?.trim().toUpperCase();
    const normalizedCountryCode = countryCode?.trim().toUpperCase();

    if (normalizedCountryCode) {
        return normalizedCountryCode === 'NL' && vatCode !== 'X';
    }

    return !vatCode || !NO_VAT_RELATION_VALUES.includes(vatCode);
};

export const normalizeAdjustments = <T,>(adjustments: T[] | Record<string, T> | null | undefined): T[] => {
    if (Array.isArray(adjustments)) {
        return adjustments.filter((item): item is T => item !== null && item !== undefined);
    }

    if (adjustments && typeof adjustments === 'object') {
        return Object.values(adjustments).filter((item): item is T => item !== null && item !== undefined);
    }

    return [];
};

export const getAdjustmentAmount = (amount: AdjustmentLike['amount']): string => {
    if (typeof amount === 'string') {
        return amount;
    }

    return amount?.number ?? '0';
};

export const getAdjustmentFormatted = (amount: AdjustmentLike['amount']): string | null => {
    if (typeof amount === 'string') {
        return null;
    }

    return amount?.formatted ?? null;
};

export const groupAdjustments = (adjustments: AdjustmentLike[] | Record<string, AdjustmentLike> | null | undefined) => {
    const normalized = normalizeAdjustments(adjustments);

    return {
        normalized,
        shippingRows: normalized.filter(item => item.type === 'shipping'),
        taxRows: normalized.filter(item => item.type === 'tax'),
        fuelRows: normalized.filter(item => item.type === 'fuel_surcharge'),
        cashInvoiceRows: normalized.filter(item => item.type === 'cash_invoice'),
    };
};
